import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Usuario } from '../models/usuario';
import { UsuarioRetornoDTO } from '../models/usuario-retorno-dto';
import { MessageProvider } from '../providers/message.provider';
import { StorageConfigProvider } from '../providers/storage-config.provider';
import { UsuarioProvider } from '../providers/usuario.provider';
import { JwtHelper } from '../utils/jwt-helper';
import { Util } from '../utils/util';
import { DominioService } from './dominio.service';
import { EnderecoService } from './endereco.service';
import { Observable, Subject } from 'rxjs';
import { Location } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class AutenticacaoService {

    private scpaUrl = environment.baseUrlScpa;
    private clientId = environment.sistemaScpa;
    private frontendUrl = window.location.origin + window.location.pathname;
    private refreshEmAndamento: Promise<void> = null;

    constructor(
        private http: HttpClient,
        private dominioService: DominioService,
        private usuarioProvider: UsuarioProvider,
        private messageProvider: MessageProvider,
        private enderecoService: EnderecoService,
        private storageConfig: StorageConfigProvider,
        private location: Location
    ) {

    }

    orderPerfis = {
        "ADM_FED": 1,
        "FED_CON": 2,
        "FED_CON_AG": 3,
        "ADM_EST": 4,
        "EST_CON": 5,
        "ADM_MUN": 6,
        "MUN_CON": 7,
        "ADS": 8,
        "ARS": 9,
        "ESTS": 10,
        "ESTNS": 11,
        "ESTNSC": 12,
        "ESTNSE": 13,
        "ESTREC": 14, // Perfil Receita Federal
    };

    perfilControleReceita = {
        id: 10059,
        sigla: "ESTNSE",
        descricao: "Estabelecimento Não SUS - CPF/CNPJ",
        tipoPerfil: "USR"
    }

    perfilReceitaLabel = "Estabelecimento Não SUS - CPF/CNPJ";

    refreshTokenHeaderParam = "X-Authorization-RefreshToken";

    private gettingOut$: Subject<string> = new Subject<string>();

    gettingOutEvent(): Observable<string> {
        return this.gettingOut$.asObservable();
    }

    login(): void {
        const frontendUrl = encodeURIComponent(`${this.frontendUrl}`);
        const autorizeUrl = `${this.scpaUrl}/oauth/authorize?redirect_uri=${frontendUrl}&force_login=true&client_id=${this.clientId}&response_type=code`;
        console.log(frontendUrl);
        this.getOut(autorizeUrl);
    }

    redirectTo(url: string): void {
        (window as Window).location.href = url;
        console.log(url);
        // (window as Window).open(url);
    }

    async getOut(url: string): Promise<void> {
        this.gettingOut$.next(url);
        await this.usuarioProvider.setUsuario(null);
        setTimeout(() => this.redirectTo(url), 1000);
    }

    async autenticar(login: string, senha: string) {
        const headers = {
            'X-Authorization': `Basic ${btoa(login + ':' + senha)}`,
            'Content-Type': 'application/x-www-form-urlencoded'
        };
        const token = await this.http.post<any>(
            environment.urlBaseNova + '/autenticacao/token-acesso', undefined, { headers }).toPromise();
        const jwtInfo: any = JwtHelper.decodeToken(token.accessToken);
        const usuarioLogado = new Usuario();
        usuarioLogado.login = login;
        usuarioLogado.token = token;
        await this.usuarioProvider.setUsuario(usuarioLogado);
        const dadosUsuario: UsuarioRetornoDTO = await this.getUsuario(jwtInfo.user_name);
        usuarioLogado.perfis = await this.getPerfisScpa(token);
        usuarioLogado.perfis.sort((a, b) => {
            if (this.orderPerfis[a.sigla] > this.orderPerfis[b.sigla]) {
                return 1;
            }
            if (this.orderPerfis[a.sigla] < this.orderPerfis[b.sigla]) {
                return -1;
            }
            return 0;
        });

        const estabelecimentosReceita = await this.getInfosReceita();

        usuarioLogado.nome = dadosUsuario.nome;
        usuarioLogado.cpf = jwtInfo.user_name;
        usuarioLogado.complemento = dadosUsuario.complemento;
        if (usuarioLogado.perfis.length > 0) {
            usuarioLogado.perfil = usuarioLogado.perfis[0];
            const resultPermissoes: any = await this.getPermissoesFuncionalidades(usuarioLogado.perfil.sigla);
            usuarioLogado.permissoes = resultPermissoes.permissoes;
            usuarioLogado.menu = await this.getMenu(usuarioLogado.perfil.sigla);
            const esferas: Array<any> = await this.getEsferas(usuarioLogado.perfil.sigla);
            usuarioLogado.esferas = [];
            console.log(esferas, 'esferas');
            for (const e of esferas) {
                const arraySplit = e.esferaAtuacao.split('/');
                const esferaAtuacao = arraySplit[arraySplit.length - 1];
                if (e.sigla === 'EST') {
                    const responseEsfera: any = await this.enderecoService.ufByCodigoIbge(esferaAtuacao);
                    responseEsfera.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(responseEsfera.record);
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'MUN') {
                    const responseEsfera: any = await this.enderecoService.municipioByCodigoIbge(esferaAtuacao);
                    responseEsfera.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(responseEsfera.record);
                    usuarioLogado.esferas.sort(Util.orderByDescricao);
                } else if (e.sigla === 'CNE') {
                    usuarioLogado.esferas.push({ nome: esferaAtuacao, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                } else if (e.sigla === 'EC' || e.sigla === 'ECPC') {
                    const strSplit = e.descricaoEsferaAtuacao.split('/');
                    let nmSplit = strSplit[strSplit.length - 1];
                    if (nmSplit.length <= 0) {
                        nmSplit = strSplit[strSplit.length - 2];
                    }
                    usuarioLogado.esferas.push({ nome: nmSplit, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'EDS') {
                    const uf: any = await this.enderecoService.ufByCodigoIbge(arraySplit[arraySplit.length - 3]);
                    const distritoSanitario: any = await this.dominioService.getDistritoByCodigo(esferaAtuacao);
                    distritoSanitario.record.siglaUf = uf.record.sigla;
                    distritoSanitario.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(distritoSanitario.record);
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'RS') {
                    const uf: any = await this.enderecoService.ufByCodigoIbge(arraySplit[arraySplit.length - 2]);
                    const regiaoSaude: any = await this.dominioService.getRegiaoSaudeByCodigo(esferaAtuacao);
                    regiaoSaude.record.siglaUf = uf.record.sigla;
                    regiaoSaude.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(regiaoSaude.record);
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'EPCP') {
                    const [cpfRaw, nomeraw] = esferaAtuacao.split("-");
                    usuarioLogado.esferas.push({ nome: Util.getMascaraCPF(cpfRaw) + " - " + nomeraw, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else {
                    usuarioLogado.esferas.push({ nome: esferaAtuacao, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                    usuarioLogado.esferas.sort(Util.orderByNome);
                }
            }

            usuarioLogado.esfera = usuarioLogado.esferas[0];
            await this.usuarioProvider.setUsuario(usuarioLogado);

            if (estabelecimentosReceita && estabelecimentosReceita.length > 0) {
                if (!usuarioLogado.perfis.find(perfil => perfil.sigla === 'ESTNSE'))
                    usuarioLogado.perfis.push(this.perfilControleReceita);
            }

            if (estabelecimentosReceita && estabelecimentosReceita.length > 0 && usuarioLogado.perfil.sigla == "ESTNSE") {
                for (const est of estabelecimentosReceita) {
                    const arraySplit = est.esferaAtuacao.split('/');
                    const esferaAtuacao = arraySplit[arraySplit.length - 1];
                    usuarioLogado.esferas.push({ nome: Util.getMascaraCNPJ(esferaAtuacao), esferaAtuacao: est.esferaAtuacao, sigla: est.sigla });
                }
            }
            console.log(usuarioLogado.esfera, 'é para cair')
            for (let e of usuarioLogado.esferas) {
                if (e.sigla === 'CNE' || e.sigla === 'CNES') {
                    const estabelecimento = await this.dominioService.estabelecimentoByCnes(e.nome);
                    e.nome = e.nome + ' - ' + estabelecimento.nomeFantasia;
                }
            }

            await this.usuarioProvider.setUsuario(usuarioLogado);
        } else {
            await this.messageProvider.showToast('Usuário não possui permissões para acesso o CNES');
            return undefined;
        }
    }

    async setRefreshtoken(refresh: string) {
        const usuario = await this.usuarioProvider.getUsuario();
        if (usuario.token) {
            usuario.token.refresh_token = refresh;
        }
        await this.usuarioProvider.setUsuario(usuario);
    }

    // O servidor rotaciona o refresh token a cada renovação: uma segunda chamada com o token antigo recebe 401.
    // Por isso chamadas concorrentes reaproveitam a mesma requisição em andamento.
    atualizarComRefreshToken(): Promise<void> {
        if (!this.refreshEmAndamento) {
            this.refreshEmAndamento = this.executarRefreshToken().then(
                () => { this.refreshEmAndamento = null; },
                (e) => { this.refreshEmAndamento = null; throw e; }
            );
        }
        return this.refreshEmAndamento;
    }

    private async executarRefreshToken() {
        if (this.usuarioProvider.getUsuario() && await this.usuarioProvider.isAutenticado()) {
            const data: any = await this.postRefreshToken();
            if (data && data.accessToken) {
                const usuario = { ...this.usuarioProvider.getUsuario() };
                const jwtInfo: any = JwtHelper.decodeToken(data.accessToken);
                usuario.login = jwtInfo.login;
                usuario.token = jwtInfo;
                usuario.token.expDate = new Date(jwtInfo.exp * 1000);
                usuario.token.accessToken = data.accessToken;
                usuario.token.refresh_token = data.refreshToken;
                await this.usuarioProvider.setUsuario(usuario);
            }
        }
    }

    async autenticarComScpa(token) {
        const jwtInfo: any = JwtHelper.decodeToken(token);
        const usuarioLogado = new Usuario();
        usuarioLogado.login = jwtInfo.login;
        usuarioLogado.token = jwtInfo;
        usuarioLogado.token.expDate = new Date(jwtInfo.exp * 1000);
        usuarioLogado.token.accessToken = token;
        await this.usuarioProvider.setUsuario(usuarioLogado);
        const dadosUsuario: UsuarioRetornoDTO = await this.getUsuario(jwtInfo.user_name);
        usuarioLogado.perfis = await this.getPerfis();
        if (usuarioLogado.perfis) {
            usuarioLogado.perfis.sort((a, b) => {
                if (this.orderPerfis[a.sigla] > this.orderPerfis[b.sigla]) {
                    return 1;
                }
                if (this.orderPerfis[a.sigla] < this.orderPerfis[b.sigla]) {
                    return -1;
                }
                return 0;
            });
        }
        const estabelecimentosReceita = await this.getInfosReceita();

        usuarioLogado.nome = dadosUsuario.nome;
        usuarioLogado.cpf = jwtInfo.cpf;
        usuarioLogado.complemento = dadosUsuario.complemento;
        if (usuarioLogado.perfis.length > 0) {
            usuarioLogado.perfil = usuarioLogado.perfis[0];
            const resultPermissoes: any = await this.getPermissoesFuncionalidades(usuarioLogado.perfil.sigla);
            usuarioLogado.permissoes = resultPermissoes.permissoes;
            usuarioLogado.menu = await this.getMenu(usuarioLogado.perfil.sigla);
            const esferas: Array<any> = await this.getEsferas(usuarioLogado.perfil.sigla);
            usuarioLogado.esferas = [];
            for (const e of esferas) {
                const arraySplit = e.esferaAtuacao.split('/');
                const esferaAtuacao = arraySplit[arraySplit.length - 1];
                if (e.sigla === 'EST') {
                    const responseEsfera: any = await this.enderecoService.ufByCodigoIbge(esferaAtuacao);
                    responseEsfera.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(responseEsfera.record);
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'MUN') {
                    const responseEsfera: any = await this.enderecoService.municipioByCodigoIbge(esferaAtuacao);
                    responseEsfera.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(responseEsfera.record);
                    usuarioLogado.esferas.sort(Util.orderByDescricao);
                } else if (e.sigla === 'CNE') {
                    usuarioLogado.esferas.push({ nome: esferaAtuacao, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                } else if (e.sigla === 'EC') {
                    const strSplit = e.descricaoEsferaAtuacao.split('/');
                    let nmSplit = strSplit[strSplit.length - 1];
                    if (nmSplit.length <= 0) {
                        nmSplit = strSplit[strSplit.length - 2];
                    }
                    usuarioLogado.esferas.push({ nome: nmSplit, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'EDS') {
                    const uf: any = await this.enderecoService.ufByCodigoIbge(arraySplit[arraySplit.length - 3]);
                    const distritoSanitario: any = await this.dominioService.getDistritoByCodigo(esferaAtuacao);
                    distritoSanitario.record.siglaUf = uf.record.sigla;
                    distritoSanitario.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(distritoSanitario.record);
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'RS') {
                    const uf: any = await this.enderecoService.ufByCodigoIbge(arraySplit[arraySplit.length - 2]);
                    const regiaoSaude: any = await this.dominioService.getRegiaoSaudeByCodigo(esferaAtuacao);
                    regiaoSaude.record.siglaUf = uf.record.sigla;
                    regiaoSaude.record.esferaAtuacao = e.esferaAtuacao;
                    usuarioLogado.esferas.push(regiaoSaude.record);
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else if (e.sigla === 'ECPC  ') {
                    const strSplit = e.descricaoEsferaAtuacao.split('/');
                    let nmSplit = strSplit[strSplit.length - 1];
                    if (nmSplit.length <= 0) {
                        nmSplit = strSplit[strSplit.length - 2];
                    }
                    const [cpfRaw, nomeraw] = nmSplit.split("-");

                    usuarioLogado.esferas.push({ nome: Util.getMascaraCPF(cpfRaw) + " - " + nomeraw, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                    usuarioLogado.esferas.sort(Util.orderByNome);
                } else {
                    usuarioLogado.esferas.push({ nome: esferaAtuacao, esferaAtuacao: e.esferaAtuacao, sigla: e.sigla });
                    usuarioLogado.esferas.sort(Util.orderByNome);
                }
            }

            usuarioLogado.esfera = usuarioLogado.esferas[0];
            await this.usuarioProvider.setUsuario(usuarioLogado);

            if (estabelecimentosReceita && estabelecimentosReceita.length > 0) {
                if (!usuarioLogado.perfis.find(perfil => perfil.sigla === 'ESTNSE'))
                    usuarioLogado.perfis.push(this.perfilControleReceita)
            }

            if (estabelecimentosReceita && estabelecimentosReceita.length > 0 && usuarioLogado.perfil.sigla == "ESTNSE") {
                for (const est of estabelecimentosReceita) {
                    const arraySplit = est.esferaAtuacao.split('/');
                    const esferaAtuacao = arraySplit[arraySplit.length - 1];
                    usuarioLogado.esferas.push({ nome: esferaAtuacao, esferaAtuacao: est.esferaAtuacao, sigla: est.sigla });
                }
            }

            for (let e of usuarioLogado.esferas) {
                if (e.sigla === 'CNE' || e.sigla === 'CNES') {
                    const estabelecimento = await this.dominioService.estabelecimentoByCnes(e.nome);
                    console.log(estabelecimento);
                    e.nome = e.nome + ' - ' + estabelecimento.nomeFantasia;
                }
            }
            usuarioLogado.esfera = usuarioLogado.esferas[0];
            await this.usuarioProvider.setUsuario(usuarioLogado);
        } else {
            await this.messageProvider.showToast('Usuário não possui permissões para acesso o CNES');
            return undefined;
        }
    }

    async logout() {
        await this.clearAuthorization();
    }

    async clearAuthorization(): Promise<void> {
        localStorage.removeItem(this.storageConfig.usuarioLogado);
    }

    async tokenDetalhe() {
        const data = await this.http.get<any>(environment.urlBaseNova + '/scpa/token-detalhe').toPromise();
        return data.authorities;
    }

    async getUsuario(cpf): Promise<any> {
        return this.http.get(environment.urlBaseNova + '/scpa/usuario?cpf=' + cpf).toPromise();
    }

    async getPerfis(): Promise<any> {
        const perfis: Array<any> = await this.http.get<Array<any>>(
            environment.urlBaseNova + '/scpa/perfis-usuario-logado').toPromise();
        return perfis.filter((p) => {
            return p.sigla !== 'ANA' && p.sigla !== 'ADM';
        });
    }

    async getMenu(siglaPerfil: string): Promise<any> {
        return this.http.get(environment.urlBaseNova + '/scpa/menu-sistema/' + siglaPerfil).toPromise();
    }

    async getEsferas(siglaPerfil: string): Promise<any> {
        return this.http.get(environment.urlBaseNova + '/scpa/esferas-usuario-logado/' + siglaPerfil).toPromise();
    }

    async getPermissoesFuncionalidades(siglaPerfil: string): Promise<any> {
        return this.http.get(environment.urlBaseNova + '/scpa/permissoes/' + siglaPerfil).toPromise();
    }

    async verificarToken(token: string): Promise<any> {
        const headers = {
            'authorization': `Bearer ${token}`,
        };
        return await this.http.get<any>(environment.urlBaseNova + '/scpa/token-detalhe', { headers }).toPromise();
    }

    async getUserScpa(cpf: string, token: string): Promise<any> {
        const headers = {
            'authorization': `Bearer ${token}`,
        };
        return this.http.get(environment.urlBaseNova + '/scpa/usuario?cpf=' + cpf, { headers }).toPromise();
    }

    async getPerfisScpa(token): Promise<any> {
        const headers = {
            'authorization': `Bearer ${token}`,
        };
        const perfis: Array<any> = await this.http.get<Array<any>>(
            environment.urlBaseNova + '/scpa/perfis-usuario-logado', { headers }).toPromise();
        return perfis.filter((p) => {
            return p.sigla !== 'ANA' && p.sigla !== 'GES' && p.sigla !== 'ADM';
        });
    }

    async getMenuScpa(siglaPerfil: string, token): Promise<any> {
        const headers = {
            'authorization': `Bearer ${token}`,
        };
        return this.http.get(environment.urlBaseNova + '/scpa/menu-sistema/' + siglaPerfil, { headers }).toPromise();
    }

    async getEsferasScpa(siglaPerfil: string, token): Promise<any> {
        const headers = {
            'authorization': `Bearer ${token}`,
        };
        return this.http.get(environment.urlBaseNova + '/scpa/esferas-usuario-logado/' + siglaPerfil, { headers }).toPromise();
    }

    async getPermissoesFuncionalidadesScpa(siglaPerfil: string, token): Promise<any> {
        const headers = {
            'authorization': `Bearer ${token}`,
        };
        return this.http.get(environment.urlBaseNova + '/scpa/permissoes/' + siglaPerfil, { headers }).toPromise();
    }

    async getInfosReceita(): Promise<any> {
        return this.http.get(environment.urlBaseNova + '/scpa/usuario/esferas-receita').toPromise();
    }

    async autenticacaoAuthCode(code: String) {
        const body = {
            code,
            redirectUrl: window.location.origin + window.location.pathname
        };
        const url = environment.urlBaseNova + "/autenticacao/auth-code";
        return this.http.post(url, body).toPromise();
    }

    async postRefreshToken() {
        const headers = {
            'authorization': `Bearer ${this.usuarioProvider.getUsuario().token.accessToken}`,
            "X-Authorization-RefreshToken": this.usuarioProvider.getUsuario().token.refresh_token,
        } as {
            [header: string]: string | string[];
        }
        const url = environment.urlBaseNova + "/autenticacao/refresh-token";
        console.log(headers);
        return this.http.post(url, {}, { headers }).toPromise();
    }
}
